import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useReviewConfig } from "@/hooks/use-review-config";
import { type ReviewCycle, useUpdateReviewCycle } from "@/hooks/use-review-cycles";

function initial(r: ReviewCycle) {
  return {
    name: r.name,
    description: r.description ?? "",
    cycleTypeKey: r.cycleTypeKey ?? "",
    periodStart: r.periodStart,
    periodEnd: r.periodEnd,
    opensOn: r.opensOn,
    dueOn: r.dueOn,
    excludeStartedAfter: r.excludeStartedAfter ?? "",
    minimumOverlapDays: r.minimumOverlapDays === null ? "" : String(r.minimumOverlapDays),
    maxEndorsements: String(r.maxEndorsements),
    managerConversationLeadDays:
      r.managerConversationLeadDays === null ? "" : String(r.managerConversationLeadDays),
  };
}

/**
 * Every setting on a review round, editable at any stage by an admin.
 *
 * Changing the period or the kind of a round that has already been generated
 * re-labels it — nobody is regenerated or re-rated — so the dialog says so
 * rather than leaving an admin to assume it rebuilds the roster.
 */
export function EditRoundSettingsDialog({
  round,
  open,
  onOpenChange,
}: {
  round: ReviewCycle;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateReviewCycle(round.id);
  const ids = {
    name: useId(),
    description: useId(),
    periodStart: useId(),
    periodEnd: useId(),
    opensOn: useId(),
    dueOn: useId(),
    cutoff: useId(),
    overlap: useId(),
    endorsements: useId(),
    leadDays: useId(),
  };

  const [form, setForm] = useState(() => initial(round));
  // Re-seed on every open so a cancelled edit never lingers.
  useEffect(() => {
    if (open) setForm(initial(round));
  }, [open, round]);
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const { data: cycleTypes = [] } = useReviewConfig("cycle-types");
  const kinds = cycleTypes.filter((kind) => kind.isActive || kind.key === round.cycleTypeKey);
  const chosen = kinds.find((kind) => kind.key === form.cycleTypeKey);

  const valid =
    form.name.trim() &&
    form.periodStart &&
    form.periodEnd &&
    form.opensOn &&
    form.dueOn &&
    form.periodEnd >= form.periodStart &&
    form.dueOn >= form.opensOn &&
    form.maxEndorsements !== "";

  const save = async () => {
    await update.mutateAsync({
      name: form.name.trim(),
      description: form.description.trim() || null,
      cycleTypeKey: form.cycleTypeKey || null,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      opensOn: form.opensOn,
      dueOn: form.dueOn,
      excludeStartedAfter: form.excludeStartedAfter || null,
      minimumOverlapDays: form.minimumOverlapDays === "" ? null : Number(form.minimumOverlapDays),
      maxEndorsements: Number(form.maxEndorsements),
      managerConversationLeadDays:
        form.managerConversationLeadDays === "" ? null : Number(form.managerConversationLeadDays),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Round settings</DialogTitle>
          <DialogDescription>
            {round.generatedAt
              ? "This round has already been generated. Changing the period or the kind re-labels it — nobody is regenerated or re-rated."
              : "Everything about this round can be changed until it is generated, and afterwards."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={ids.name}>Name</Label>
            <Input id={ids.name} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={ids.description}>Description</Label>
            <Textarea
              id={ids.description}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="font-medium text-sm">Kind of round</legend>
            <div className="flex flex-wrap gap-2">
              {kinds.map((kind) => (
                <button
                  key={kind.key}
                  type="button"
                  aria-pressed={kind.key === form.cycleTypeKey}
                  onClick={() => set("cycleTypeKey", kind.key)}
                  className={`rounded-full border px-3 py-1.5 font-medium text-sm transition-colors ${
                    kind.key === form.cycleTypeKey
                      ? "border-primary bg-accent text-primary"
                      : "border-border hover:bg-muted/60"
                  }`}
                >
                  {kind.name}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={ids.periodStart}>Period reviewed, from</Label>
              <Input
                id={ids.periodStart}
                type="date"
                value={form.periodStart}
                onChange={(e) => set("periodStart", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.periodEnd}>to</Label>
              <Input
                id={ids.periodEnd}
                type="date"
                value={form.periodEnd}
                onChange={(e) => set("periodEnd", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.opensOn}>Reviewing opens</Label>
              <Input
                id={ids.opensOn}
                type="date"
                value={form.opensOn}
                onChange={(e) => set("opensOn", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.dueOn}>Responses due</Label>
              <Input
                id={ids.dueOn}
                type="date"
                value={form.dueOn}
                onChange={(e) => set("dueOn", e.target.value)}
              />
            </div>
          </div>
          {chosen?.coversEveryone !== false && (
            <div className="space-y-2">
              <Label htmlFor={ids.cutoff}>Leave out anyone who started after</Label>
              <Input
                id={ids.cutoff}
                type="date"
                value={form.excludeStartedAfter}
                onChange={(e) => set("excludeStartedAfter", e.target.value)}
              />
              <p className="text-muted-foreground text-sm">
                Only applies when the roster is built, so it won't remove anyone already in a
                generated round.
              </p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor={ids.overlap}>Shared project hours for a peer</Label>
              <Input
                id={ids.overlap}
                type="number"
                min={0}
                value={form.minimumOverlapDays}
                onChange={(e) => set("minimumOverlapDays", e.target.value)}
                placeholder="Off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.endorsements}>Endorsements per review</Label>
              <Input
                id={ids.endorsements}
                type="number"
                min={0}
                max={20}
                value={form.maxEndorsements}
                onChange={(e) => set("maxEndorsements", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.leadDays}>Conversation reminder, days early</Label>
              <Input
                id={ids.leadDays}
                type="number"
                min={0}
                max={90}
                value={form.managerConversationLeadDays}
                onChange={(e) => set("managerConversationLeadDays", e.target.value)}
                placeholder="Off"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!valid || update.isPending}>
            Save settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
